import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { WebHelperService } from '@libs/libraries/frontend';
import { EnumNgFaSize, NgModalAssistantService } from '@libs/frameworks/angular';
import { SparkSidebarNavigationService } from '@domains/frontend/templates/spark';
import { FLYING_APP_CONSTANTS } from '@apps-v2/flying/shared';

import { AuthSecurityService } from '../../../services/shared/auth/auth-security/auth-security.service';
import { AppearancePreferenceService } from '../../../services/shared/layout-preferences/appearance-preference.service';
import { APPEARANCE_PREFERENCE_VISIBILITY_ANIMATION } from '../../../services/shared/layout-preferences/appearance-preference.animation';
import { LocalStorageService } from '../../../services/shared/local-storage/local-storage.service';
import { EnumInfoMessages } from '../../../services/shared/local-storage/sub-storages/app.interface';
import { StatesService } from '../../../services/shared/states/states.service';
import { EnumWebAppTool, IWebAppTool } from '../../../shared/constants/app.constant';
import { WelcomeTourComponent } from '../../../shared/modals/welcome-modals/welcome-tour-modal/welcome-tour.component';
import { ENVIRONMENT } from '../../../../environments';
import { EnumResearchTourElement } from '../../../shared/modals/welcome-modals/welcome-tour-modal/data/research-tour.interface';
import { IUserInfo } from '../../../requests/shared/user/user.request';


@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
	animations: [APPEARANCE_PREFERENCE_VISIBILITY_ANIMATION]
})
export class HeaderComponent implements OnInit {

	@Input() agentPanelOpen: boolean = false;
	@Input() publicView: boolean = false;
	@Input() returnUrl: string | UrlTree = '/';
	@Output() readonly agentPanelToggle: EventEmitter<void> = new EventEmitter<void>();
	@ViewChild('agentLauncher') agentLauncher: ElementRef<HTMLButtonElement>;

	//** Configurations */
	readonly VERSION		  		: string = ENVIRONMENT.VERSION;
	readonly IS_WHATS_NEW_ENABLED	: boolean = ENVIRONMENT.FEATURE_FLAGS.WHATS_NEW;
	readonly DISCORD_URL	  		: string = FLYING_APP_CONSTANTS.SOCIAL_LINKS.DISCORD;
	readonly AFFILIATE_PROGRAM 		: string = FLYING_APP_CONSTANTS.HOMEPAGE_LINKS.AFFILIATE_PROGRAM;
	readonly WHATS_NEW_UPLOAD_URL	: string = FLYING_APP_CONSTANTS.WHATS_NEW.UPLOAD;
	readonly WHATS_NEW_RESEARCH_URL	: string = FLYING_APP_CONSTANTS.WHATS_NEW.RESEARCH;

	//** Getter Helpers */
	readonly EnumNgFaSize			: typeof EnumNgFaSize = EnumNgFaSize;
	readonly EnumResearchTourElement: typeof EnumResearchTourElement = EnumResearchTourElement;
	readonly EnumWebAppTool			: typeof EnumWebAppTool = EnumWebAppTool;

	//** App switcher */
	toggleAppLogo		: string = 'assets/upload/logos/upload-logo.svg';
	toggleAppLabel		: string = 'Upload';
	toggleAppTool		: EnumWebAppTool = EnumWebAppTool.Upload;
	tokensBalance 		: number = 0;
	userInfo			: IUserInfo = {
		firstname	: '',
		lastname	: '',
		email		: '',
		avatar		: 6,
		roles		: []
	};

	//** Show promotion / whats new flags */
	showPromotion		: boolean = false;
	showChristmasImage	: boolean = true;
	highlightWhatsNew	: boolean = true;
	isSigningOut		: boolean = false;
	isFullScreenEnabled: boolean = true;


	constructor(
		private router					: Router,
		private statesService			: StatesService,
		private cdr						: ChangeDetectorRef,
		private webHelperService 		: WebHelperService,
		private authSecurityService		: AuthSecurityService,
		private localStorageService		: LocalStorageService,
		private modalAssistantService	: NgModalAssistantService,
		public appearancePreferenceService: AppearancePreferenceService,
		public  sidebarNavigationService: SparkSidebarNavigationService
	) {}


	ngOnInit(): void {
		if (this.publicView) return;

		let isInitialFullScreenPreferenceEmission: boolean = true;

		// Keep the full-screen control in sync with the Appearance preference.
		this.appearancePreferenceService.fullScreenEnabled$.subscribe((isEnabled: boolean) => {
			this.isFullScreenEnabled = isEnabled;

			if (isInitialFullScreenPreferenceEmission) {
				isInitialFullScreenPreferenceEmission = false;
				return;
			}

			this.cdr.detectChanges();
		});

		//0 - update toggle icon on change in selected app
		this.statesService.appState.getSelectedAppObservable().subscribe((selectedApp: IWebAppTool) => {

			//a. do nothing if selected app is not set
			if (!selectedApp) return;

			//b. update toggle icon with unselected app tool
			const unselectedApp: EnumWebAppTool = selectedApp.tool === EnumWebAppTool.Research ? EnumWebAppTool.Upload : EnumWebAppTool.Research;
			const targetApp: IWebAppTool = this.statesService.appState.getAppConfig(unselectedApp);
			this.toggleAppLogo = targetApp.logo;
			this.toggleAppLabel = targetApp.name.replace('Flying ', '');
			this.toggleAppTool = targetApp.tool;

			//c. rerender
			this.cdr.markForCheck();
		});

		//1 - update tokens count & check date for promotion
		this.statesService.tokens.tokenBalance$.subscribe((newTokenBalance: number) => {
			this.tokensBalance = newTokenBalance;
			this.cdr.markForCheck();
		});
		this.statesService.user.getUserInfo().subscribe((userInfo: IUserInfo) => {
			this.userInfo = userInfo;
			this.cdr.markForCheck();
		});
		this.showPromotion = new Date() <= new Date(2021, 2, 8);

		//2 - should the whats new be blinking
		this.highlightWhatsNew = this.notOpenedInVersion();
	}

	//** Sign out */
	signOut(): void {
		if (this.isSigningOut) return;

		this.isSigningOut = true;
		this.cdr.markForCheck();
		this.authSecurityService.fullSignout().then(() => {
			this.isSigningOut = false;
			this.cdr.markForCheck();
		});
	}

	//** Toggle tool */
	toggleTool(): void {

		//0 - determine the destination tool
		const selectedApp: EnumWebAppTool = this.statesService.appState.getSelectedApp().tool;
		const targetApp: EnumWebAppTool = selectedApp === EnumWebAppTool.Upload
			? EnumWebAppTool.Research
			: EnumWebAppTool.Upload;

		//1 - update the selected tool so the current shell can refresh in place
		this.statesService.appState.setSelectedApp(targetApp);

		//2 - update ui infos
		this.highlightWhatsNew = this.notOpenedInVersion();

		//3 - keep shared settings views open while their tool-specific navigation updates
		const currentRoute: string = this.router.url.split(/[?#]/, 1)[0];
		if (currentRoute === '/settings' || currentRoute.startsWith('/settings/')) {

			// Research notification settings do not apply to Flying Upload.
			if (targetApp === EnumWebAppTool.Upload && currentRoute.startsWith('/settings/research/')) {
				this.router.navigate(['/settings/appearance']);
			}
			return;
		}

		//4 - other application pages continue to switch through the tool home
		this.router.navigate(['/']);
	}

	/**------------------------------------------------------
	 * Whats New (show based on Version)
	 */
	private notOpenedInVersion(): boolean {

		//0 - get the current version
		let localVersion: string = null;
		const selectedApp: EnumWebAppTool = this.statesService.appState.getSelectedApp().tool;
		switch (selectedApp) {
			case EnumWebAppTool.Research:
				localVersion = this.localStorageService.app.getInfoMessageValue<string>(EnumInfoMessages.WhatsNewResearch);
				break;

			case EnumWebAppTool.Upload:
				localVersion = this.localStorageService.app.getInfoMessageValue<string>(EnumInfoMessages.WhatsNewUpload);
				break;

			default:
				throw new Error(`HeaderComponent => notOpenedInVersion => FATAL ERROR: ${selectedApp} is invalid for the version info`);
		}

		//1 - compare it against the local storage entry
		return localVersion !== this.VERSION;
	}

	openWhatsNewStatus(): void {

		//0 - set selected app and version
		let url: string = '';
		const selectedApp: EnumWebAppTool = this.statesService.appState.getSelectedApp().tool;
		switch (selectedApp) {
			case EnumWebAppTool.Research:
				this.localStorageService.app.setInfoMessageValue(EnumInfoMessages.WhatsNewResearch, this.VERSION);
				url = this.WHATS_NEW_RESEARCH_URL;
				break;

			case EnumWebAppTool.Upload:
				this.localStorageService.app.setInfoMessageValue(EnumInfoMessages.WhatsNewUpload, this.VERSION);
				url = this.WHATS_NEW_UPLOAD_URL;
				break;

			default:
				throw new Error(`HeaderComponent => openWhatsNewStatus => FATAL ERROR: ${selectedApp} is invalid for the version info`);
		}

		//1 - open what's new tab
		this.highlightWhatsNew = this.notOpenedInVersion();
		this.webHelperService.openNewTab(url);
	}

	reportBug(): void {
		this.router.navigate(['support', 'report']);
	}

	focusAgentLauncher(): void {
		this.agentLauncher.nativeElement.focus();
	}


	/**------------------------------------------------------
	 * Open Welcome Tour
	 */
	openWelcomeTour(): void {

		// open the welcome modal
		this.modalAssistantService.openLarge(WelcomeTourComponent);
	}
}
